import {getDecimalSeparatorIndex, getFractionalPartInfo} from './utils';
import {POSSIBLE_DECIMAL_SEPARATORS, POSSIBLE_MINUS_KEYS} from './constants';

export type ResolverConfig = {
  locale: string;
  fractionalDigits: number;
  groupSeparator: string;
  decimalSeparator: string;
  minusSign: string;
  allowNegative: boolean;
  showEmptyFractionDigits: boolean;
  forceMaximumFractionDigits: boolean;
  suppressGroupSeparatorAsDecimal: boolean;
};

export type InputState = {
  formattedValue: string;
  numericValue: number | null;
  hasFractionalDigits: boolean;
};

export type Selection = [start: number, end: number];

export type StateResult = {
  formattedValue: string;
  cursor: number;
  hasFractionalDigits: boolean;
};

export type CursorResult = {
  cursor: number;
};

export type ResolverResult = StateResult | CursorResult | 'suppress' | null;

export function isStateResult(result: ResolverResult): result is StateResult {
  return result !== null && result !== 'suppress' && 'formattedValue' in result;
}

// ---------------------------------------------------------------------------
// Sub-handlers
// ---------------------------------------------------------------------------

function resolveDelete(
  key: string,
  selection: Selection,
  state: InputState,
  config: ResolverConfig,
): ResolverResult | undefined {
  const isDeleting = key === 'Backspace' || key === 'Delete';
  if (!isDeleting) {
    return undefined;
  }

  const {decimalSeparator, groupSeparator, showEmptyFractionDigits} = config;
  const [selectionStart, selectionEnd] = selection;
  const {formattedValue} = state;
  const {decimalIndex, hasFractionalPart, firstTrailingZeroIndex} =
    getFractionalPartInfo(formattedValue, config.locale);
  const isSelection = selectionStart !== selectionEnd;
  const isNeg =
    config.allowNegative && formattedValue.startsWith(config.minusSign);
  const signLen = isNeg ? config.minusSign.length : 0;

  if (isSelection) {
    if (hasFractionalPart && selectionStart >= firstTrailingZeroIndex) {
      const newCursor = key === 'Delete' ? selectionEnd : selectionStart;
      return {cursor: newCursor};
    }
    return undefined;
  }

  if (isNeg) {
    const affectedIdx =
      key === 'Backspace' ? selectionStart - 1 : selectionStart;
    if (affectedIdx >= 0 && affectedIdx < signLen) {
      const newFormattedValue = formattedValue.slice(signLen);
      return {
        formattedValue: newFormattedValue,
        cursor: 0,
        hasFractionalDigits: state.hasFractionalDigits,
      };
    }
  }

  if (
    formattedValue[signLen] === '0' &&
    state.numericValue !== null &&
    state.numericValue !== 0
  ) {
    if (
      decimalIndex === signLen + 1 &&
      ((key === 'Delete' && selectionStart === signLen) ||
        (key === 'Backspace' && selectionStart === signLen + 1))
    ) {
      const newCursor = key === 'Delete' ? signLen + 1 : signLen;
      return {cursor: newCursor};
    }
  }

  const isZeroValue = state.numericValue === 0 || state.numericValue === null;
  const isCursorAtStart =
    selectionStart === signLen || selectionStart === signLen + 1;
  if (isZeroValue && isCursorAtStart) {
    return {formattedValue: '', cursor: 0, hasFractionalDigits: false};
  }

  const deleteDirection = key === 'Backspace' ? -1 : 1;
  const affectedCharIndex = selectionStart + (deleteDirection === 1 ? 0 : -1);
  const affectedChar = formattedValue[affectedCharIndex];
  const [integerPart = '', fractionalPart] =
    formattedValue.split(decimalSeparator);
  const isFractionalPartZero =
    fractionalPart != null ? Number.parseInt(fractionalPart, 10) === 0 : false;

  // The integer part includes the minus sign — the raw digit length excludes it.
  const integerDigitLen = integerPart.length - signLen;

  // Intercept deleting the only integer digit of X.00 (or -X.00) — otherwise
  // the browser removes it, leaving ".00" (which silently becomes 0) or "-" (NaN).
  if (
    hasFractionalPart &&
    isFractionalPartZero &&
    integerDigitLen === 1 &&
    affectedCharIndex === signLen
  ) {
    return {formattedValue: '', cursor: 0, hasFractionalDigits: false};
  }

  // Intercept deleting the only digit of a single-digit value (e.g. "5" or "-5")
  // when there's no fractional part — same risk of leaving an empty/minus-only string.
  if (
    !hasFractionalPart &&
    integerDigitLen === 1 &&
    affectedCharIndex === signLen
  ) {
    return {formattedValue: '', cursor: 0, hasFractionalDigits: false};
  }

  const skipCursor = (direction: 1 | -1): CursorResult => ({
    cursor: selectionStart + direction,
  });

  if (affectedChar === decimalSeparator) {
    if (isFractionalPartZero && !showEmptyFractionDigits) {
      // All-zero fractional part and fractions aren't pinned — collapse decimal mode.
      return {
        formattedValue: integerPart ?? '',
        cursor: integerPart?.length ?? 0,
        hasFractionalDigits: false,
      };
    } else {
      // Decimal separator is protected (pinned fractions or significant digits).
      return skipCursor(deleteDirection);
    }
  }

  if (affectedChar === groupSeparator) {
    return skipCursor(deleteDirection);
  }

  if (
    hasFractionalPart &&
    affectedChar === '0' &&
    affectedCharIndex > decimalIndex
  ) {
    if (isFractionalPartZero) {
      if (showEmptyFractionDigits || config.forceMaximumFractionDigits) {
        // Fractions are pinned — skip past the zero to preserve decimal display.
        return skipCursor(deleteDirection);
      }
      // Collapse fractional part by returning a StateResult directly (no
      // parseFormattedNumber round-trip needed — integerPart is already clean).
      return {
        formattedValue: integerPart ?? '',
        cursor: integerPart?.length ?? 0,
        hasFractionalDigits: false,
      };
    } else if (affectedCharIndex >= firstTrailingZeroIndex) {
      return skipCursor(deleteDirection);
    }
  }

  return undefined;
}

function resolveGroupSeparator(
  key: string,
  selection: Selection,
  state: InputState,
  config: ResolverConfig,
): ResolverResult | undefined {
  const [selectionStart, selectionEnd] = selection;

  if (selectionStart !== selectionEnd) {
    return undefined;
  }

  if (
    key === config.groupSeparator &&
    state.formattedValue[selectionStart] === config.groupSeparator
  ) {
    return {cursor: selectionStart + 1};
  }

  return undefined;
}

function resolveDecimalSeparator(
  key: string,
  selection: Selection,
  state: InputState,
  config: ResolverConfig,
): ResolverResult | undefined {
  if (!POSSIBLE_DECIMAL_SEPARATORS.has(key)) {
    return undefined;
  }

  // "." is universally understood as decimal on keyboards regardless of locale.
  // Other separators (e.g. ",") only trigger decimal when they ARE the locale's
  // decimal separator — otherwise they're the group separator and get suppressed.
  // Fields that use group separators in display (e.g. CurrencyField) set
  // suppressGroupSeparatorAsDecimal to prevent ambiguity with thousands commas.
  if (
    config.suppressGroupSeparatorAsDecimal &&
    key !== config.decimalSeparator &&
    key !== '.'
  ) {
    return 'suppress';
  }

  const {fractionalDigits, decimalSeparator, locale} = config;
  const [selectionStart, selectionEnd] = selection;

  if (fractionalDigits === 0) {
    return 'suppress';
  }

  if (
    state.formattedValue === '' ||
    state.formattedValue === '0' ||
    (selectionStart === 0 && selectionEnd === state.formattedValue.length)
  ) {
    const newFormattedValue = `0${decimalSeparator}${'0'.repeat(
      fractionalDigits,
    )}`;
    return {
      formattedValue: newFormattedValue,
      cursor: 2,
      hasFractionalDigits: true,
    };
  }

  if (state.formattedValue[selectionStart] === decimalSeparator) {
    return {cursor: selectionStart + 1};
  }

  if (getDecimalSeparatorIndex(state.formattedValue, locale) !== -1) {
    return 'suppress';
  }

  if (state.formattedValue.length === selectionStart) {
    const newFormattedValue =
      state.formattedValue + decimalSeparator + '0'.repeat(fractionalDigits);
    return {
      formattedValue: newFormattedValue,
      cursor: selectionStart + 1,
      hasFractionalDigits: true,
    };
  }

  const newFormattedValue =
    state.formattedValue.slice(0, selectionStart) +
    decimalSeparator +
    state.formattedValue.slice(selectionStart);
  const decimalIndex = getDecimalSeparatorIndex(newFormattedValue, locale);
  const signOffset =
    config.allowNegative && state.formattedValue.startsWith(config.minusSign)
      ? config.minusSign.length
      : 0;
  return {
    formattedValue: newFormattedValue,
    cursor: Math.max(signOffset + 2, decimalIndex + 1),
    hasFractionalDigits: true,
  };
}

function resolveZero(
  key: string,
  selection: Selection,
  state: InputState,
  config: ResolverConfig,
): ResolverResult | undefined {
  if (key !== '0') {
    return undefined;
  }

  const {locale, showEmptyFractionDigits} = config;
  const [selectionStart, selectionEnd] = selection;
  const {decimalIndex, hasFractionalPart, firstTrailingZeroIndex} =
    getFractionalPartInfo(state.formattedValue, locale);

  if (selectionStart !== selectionEnd) {
    const isFractionalPartSelected =
      hasFractionalPart && selectionStart > decimalIndex;
    if (isFractionalPartSelected && selectionStart >= firstTrailingZeroIndex) {
      return {cursor: selectionEnd};
    }
    return undefined;
  }

  if (state.formattedValue === '') {
    return {
      formattedValue: '0',
      cursor: 1,
      hasFractionalDigits: showEmptyFractionDigits,
    };
  }

  if (hasFractionalPart && selectionStart >= firstTrailingZeroIndex) {
    // Only skip the zero if the fractional part is already at full capacity.
    // If there's room for more digits, let the input through so the user
    // can extend precision (e.g. fractionDigitOffset scenarios).
    const fractionalPartLength = state.formattedValue.length - decimalIndex - 1;
    if (fractionalPartLength >= config.fractionalDigits) {
      return {cursor: selectionStart + 1};
    }
    return undefined;
  }

  const hasMinusPrefix =
    config.allowNegative && state.formattedValue.startsWith(config.minusSign);
  const zeroBlockPos = hasMinusPrefix ? config.minusSign.length : 0;
  if (selectionStart <= zeroBlockPos) {
    return {cursor: zeroBlockPos};
  }

  return undefined;
}

function resolveLeadingZeroReplacement(
  key: string,
  selection: Selection,
  state: InputState,
  config: ResolverConfig,
): ResolverResult | undefined {
  const [selectionStart, selectionEnd] = selection;

  if (selectionStart !== selectionEnd) {
    return undefined;
  }

  const {formattedValue} = state;
  const {decimalSeparator, minusSign, allowNegative} = config;
  const isNegative = allowNegative && formattedValue.startsWith(minusSign);
  const offset = isNegative ? minusSign.length : 0;

  if (
    /^[1-9]$/.test(key) &&
    (selectionStart === offset || selectionStart === offset + 1) &&
    formattedValue[offset] === '0'
  ) {
    const [, fractionalPart = ''] = formattedValue.split(decimalSeparator);
    const prefix = isNegative ? minusSign : '';
    const hasFractional = fractionalPart.length > 0;
    const newFormatted = hasFractional
      ? prefix + key + decimalSeparator + fractionalPart
      : prefix + key;
    return {
      formattedValue: newFormatted,
      cursor: offset + 1,
      hasFractionalDigits: hasFractional,
    };
  }

  return undefined;
}

function resolveMinus(
  key: string,
  selection: Selection,
  state: InputState,
  config: ResolverConfig,
): ResolverResult | undefined {
  if (!POSSIBLE_MINUS_KEYS.has(key) && key !== config.minusSign) {
    return undefined;
  }

  if (!config.allowNegative) {
    return 'suppress';
  }

  const {formattedValue} = state;
  const {minusSign} = config;

  if (formattedValue === '') {
    return {
      formattedValue: minusSign,
      cursor: minusSign.length,
      hasFractionalDigits: state.hasFractionalDigits,
    };
  }

  const [selectionStart] = selection;
  const isNegative = formattedValue.startsWith(minusSign);

  if (isNegative) {
    const newFormattedValue = formattedValue.slice(minusSign.length);
    const newCursor = Math.max(0, selectionStart - minusSign.length);
    return {
      formattedValue: newFormattedValue,
      cursor: newCursor,
      hasFractionalDigits: state.hasFractionalDigits,
    };
  } else {
    const newFormattedValue = minusSign + formattedValue;
    const isZeroValue =
      state.numericValue === 0 || Object.is(state.numericValue, -0);
    const newCursor = isZeroValue
      ? newFormattedValue.length
      : selectionStart + minusSign.length;
    return {
      formattedValue: newFormattedValue,
      cursor: newCursor,
      hasFractionalDigits: state.hasFractionalDigits,
    };
  }
}

// ---------------------------------------------------------------------------
// Main dispatcher
// ---------------------------------------------------------------------------

export function resolveKeyDown(
  state: InputState,
  key: string,
  selection: Selection,
  config: ResolverConfig,
): ResolverResult {
  const handlers = [
    resolveDelete,
    resolveGroupSeparator,
    resolveDecimalSeparator,
    resolveZero,
    resolveLeadingZeroReplacement,
    resolveMinus,
  ];

  for (const handler of handlers) {
    const result = handler(key, selection, state, config);
    if (result !== undefined) {
      return result;
    }
  }

  if (key.length === 1) {
    const isValid =
      /^[0-9]$/.test(key) ||
      POSSIBLE_DECIMAL_SEPARATORS.has(key) ||
      key === config.groupSeparator;
    if (!isValid) {
      return 'suppress';
    }
  }

  return null;
}
